/**
 * PlexCredentialFields
 *
 * The plex.tv Email + Password (show/hide) + optional 2FA code inputs, shared by
 * the inline install-dialog credential section and the standalone
 * PlexCredentialModal. Extracting the markup keeps both surfaces visually and
 * a11y-identical.
 *
 * Security contract (inherited by both callers):
 *   - Password and OTP are NEVER logged to the console
 *   - Values are NEVER written to localStorage or sessionStorage
 *   - The parent holds the values in component state and sends them once via a
 *     POST body, then discards them
 */
import { type Ref } from "react";
import { Icon } from "../ui/Icon";
import { Input } from "../ui/Input";

type PlexCredentialFieldsProps = {
  email: string;
  password: string;
  otp: string;
  showPassword: boolean;
  /** Disables every input (e.g. while a request is in flight). */
  disabled?: boolean;
  /** Optional ref for the email input so callers can autofocus it. */
  emailRef?: Ref<HTMLInputElement>;
  /**
   * Suffix appended to each input id so multiple instances (inline + modal) keep
   * unique ids. Defaults to "modal".
   */
  idScope?: string;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onOtpChange: (value: string) => void;
  onToggleShowPassword: () => void;
};

export const PlexCredentialFields = ({
  email,
  password,
  otp,
  showPassword,
  disabled = false,
  emailRef,
  idScope = "modal",
  onEmailChange,
  onPasswordChange,
  onOtpChange,
  onToggleShowPassword,
}: PlexCredentialFieldsProps) => {
  const emailId = `plex-email-${idScope}`;
  const passwordId = `plex-password-${idScope}`;
  const otpId = `plex-otp-${idScope}`;

  return (
    <div className="space-y-4">
      {/* Email */}
      <div className="space-y-1.5">
        <label htmlFor={emailId} className="block text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Plex.tv Email <span className="text-destructive" aria-hidden="true">*</span>
        </label>
        <Input
          ref={emailRef}
          id={emailId}
          type="email"
          autoComplete="username"
          value={email}
          onChange={(e) => onEmailChange(e.target.value)}
          placeholder="you@example.com"
          disabled={disabled}
          required
        />
      </div>

      {/* Password */}
      <div className="space-y-1.5">
        <label htmlFor={passwordId} className="block text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Password <span className="text-destructive" aria-hidden="true">*</span>
        </label>
        <div className="relative">
          <Input
            id={passwordId}
            type={showPassword ? "text" : "password"}
            autoComplete="current-password"
            value={password}
            onChange={(e) => onPasswordChange(e.target.value)}
            placeholder="Your plex.tv password"
            disabled={disabled}
            required
            className="pr-10"
          />
          <button
            type="button"
            onClick={onToggleShowPassword}
            disabled={disabled}
            aria-label={showPassword ? "Hide password" : "Show password"}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 rounded-sm"
          >
            <Icon icon={showPassword ? "lucide:eye-off" : "lucide:eye"} className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* 2FA code — optional */}
      <div className="space-y-1.5">
        <label htmlFor={otpId} className="block text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Two-Factor Code{" "}
          <span className="normal-case font-normal text-muted-foreground/60">(leave blank if no 2FA)</span>
        </label>
        <Input
          id={otpId}
          type="text"
          autoComplete="one-time-code"
          inputMode="numeric"
          value={otp}
          onChange={(e) => onOtpChange(e.target.value)}
          placeholder="6-digit code (optional)"
          disabled={disabled}
          maxLength={16}
        />
      </div>
    </div>
  );
};
